export type ProfileHostUser = Record<string, unknown> | null | undefined;
export type ProfileHostBranch = Record<string, unknown> | null | undefined;

export type ProfileHostModalOptions = {
  templateUrl?: string;
  controller?: string;
  controllerAs?: string;
  inputs?: Record<string, unknown>;
};

/**
 * Angular-owned ports for `useProfile`. Profile cancel and PAYG purchase HTTP
 * live in `profilePurchase`; the remaining reads stay in the Angular factories.
 */
export type ProfileHostDependencies = {
  getCurrentUser: () => ProfileHostUser;
  getCurrentBranch: () => ProfileHostBranch;
  isFeatureFlagEnabled: (name: string) => boolean;
  modifyProfile: (userId: string, changes: unknown, image?: File | Blob | null) => Promise<unknown>;
  getUserConsentObjectUpdated: (consents: unknown) => unknown;
  getTransactionsById: (userId: string, page?: number) => Promise<unknown>;
  getDocumentsByUserId: (branchId: string, userId: string) => Promise<unknown>;
  getBranchDocuments: (branchId: string) => Promise<unknown>;
  getBookingsById: (userId: string, includeSpotBookings?: boolean) => Promise<unknown>;
  getLinkedAccount: (userId: string) => Promise<unknown>;
  getUsersStackedMemberships: (branchId: string, userId: string) => Promise<unknown>;
  cancelBookingById: (userId: string, bookingId: string) => Promise<unknown>;
  buyMembershipPlan: (...args: unknown[]) => Promise<unknown>;
  logOut: () => void | Promise<unknown>;
  showModal: (options: ProfileHostModalOptions) => Promise<unknown>;
  triggerAnalytics: (event: Record<string, unknown>) => void;
  onUserChange: (callback: () => void) => () => void;
  onFeatureFlagsLoaded: (callback: () => void) => () => void;
  getCancelRequestIds: () => unknown;
  setCancelRequestIds: (value: unknown) => void;
  onCancelRequestIdsChange: (callback: (value: unknown) => void) => () => void;
  fetchAllFeatureFlags: () => void;
  getAvailableCreditsById: (userId: string) => Promise<unknown>;
  getBranchById: (branchId: string) => Promise<unknown>;
  getTermsAndConditions: (branchId: string, type: string) => Promise<unknown>;
  getUserConsents: (userId: string) => Promise<unknown>;
  hydrateChannelConsentPurposeStatuses: (channel: unknown) => void;
  deleteCardDetails: (userId: string) => Promise<unknown>;
  deleteDirectDebitDetails: (userId: string, mandateId: string) => Promise<unknown>;
  getUserById: (userId: string) => Promise<unknown>;
  setCurrentUser: (user: unknown) => void;
  setStoredUser: (user: unknown) => void;
  getChildAccountToken: (parentId: string, childId: string) => Promise<unknown>;
  setCurrentToken: (token: string, kind: string) => void;
  removeChildToken: () => void;
  setPaymentMethods: () => void;
  emitUserChange: () => void;
  emitLoading: (payload: { loader: string; loading: boolean }) => void;
  getDocumentUrl: (
    branchId: string,
    userId: string,
    documentId: string,
    userMembershipId: string | false
  ) => string;
  resolveBookedSpotName: (booking: unknown) => string;
  isSpotBookingEnabledForBooking: (booking: unknown) => boolean;
  formatUnix: (timestamp: number, format: string) => string;
  formatLocalDate: (value: unknown, format: string) => string;
  formatMoney: (amount: number, currency: string) => string;
  ordinal: (value: number) => string;
  currencySymbol: () => string;
  todayLocal: () => string;
  scrollIframeToTop: () => void;
  consentTranslationParams: () => { branchName?: string };
  translatePlanDuration: (value: unknown, unit: unknown) => string;
};

export type ProfileHostPorts = {
  isReactProfileDialogEnabled: () => boolean;
  isFeatureFlagEnabled: (name: string) => boolean;
  getCurrentUser: () => ProfileHostUser;
  getCurrentBranch: () => ProfileHostBranch;
  modifyProfile: ProfileHostDependencies['modifyProfile'];
  getUserConsentObjectUpdated: ProfileHostDependencies['getUserConsentObjectUpdated'];
  getTransactionsById: ProfileHostDependencies['getTransactionsById'];
  getDocumentsByUserId: ProfileHostDependencies['getDocumentsByUserId'];
  getBranchDocuments: ProfileHostDependencies['getBranchDocuments'];
  getBookingsById: ProfileHostDependencies['getBookingsById'];
  getLinkedAccount: ProfileHostDependencies['getLinkedAccount'];
  getUsersStackedMemberships: ProfileHostDependencies['getUsersStackedMemberships'];
  cancelBookingById: ProfileHostDependencies['cancelBookingById'];
  buyMembershipPlan: ProfileHostDependencies['buyMembershipPlan'];
  logOut: ProfileHostDependencies['logOut'];
  showModal: ProfileHostDependencies['showModal'];
  triggerAnalytics: ProfileHostDependencies['triggerAnalytics'];
  onUserChange: ProfileHostDependencies['onUserChange'];
  onFeatureFlagsLoaded: ProfileHostDependencies['onFeatureFlagsLoaded'];
  getCancelRequestIds: ProfileHostDependencies['getCancelRequestIds'];
  setCancelRequestIds: ProfileHostDependencies['setCancelRequestIds'];
  onCancelRequestIdsChange: ProfileHostDependencies['onCancelRequestIdsChange'];
  fetchAllFeatureFlags: ProfileHostDependencies['fetchAllFeatureFlags'];
  getAvailableCreditsById: ProfileHostDependencies['getAvailableCreditsById'];
  getBranchById: ProfileHostDependencies['getBranchById'];
  getTermsAndConditions: ProfileHostDependencies['getTermsAndConditions'];
  getUserConsents: ProfileHostDependencies['getUserConsents'];
  hydrateChannelConsentPurposeStatuses: ProfileHostDependencies['hydrateChannelConsentPurposeStatuses'];
  deleteCardDetails: ProfileHostDependencies['deleteCardDetails'];
  deleteDirectDebitDetails: ProfileHostDependencies['deleteDirectDebitDetails'];
  getUserById: ProfileHostDependencies['getUserById'];
  setCurrentUser: ProfileHostDependencies['setCurrentUser'];
  setStoredUser: ProfileHostDependencies['setStoredUser'];
  getChildAccountToken: ProfileHostDependencies['getChildAccountToken'];
  setCurrentToken: ProfileHostDependencies['setCurrentToken'];
  removeChildToken: ProfileHostDependencies['removeChildToken'];
  setPaymentMethods: ProfileHostDependencies['setPaymentMethods'];
  emitUserChange: ProfileHostDependencies['emitUserChange'];
  emitLoading: ProfileHostDependencies['emitLoading'];
  getDocumentUrl: ProfileHostDependencies['getDocumentUrl'];
  resolveBookedSpotName: ProfileHostDependencies['resolveBookedSpotName'];
  isSpotBookingEnabledForBooking: ProfileHostDependencies['isSpotBookingEnabledForBooking'];
  formatUnix: ProfileHostDependencies['formatUnix'];
  formatLocalDate: ProfileHostDependencies['formatLocalDate'];
  formatMoney: ProfileHostDependencies['formatMoney'];
  ordinal: ProfileHostDependencies['ordinal'];
  currencySymbol: ProfileHostDependencies['currencySymbol'];
  todayLocal: ProfileHostDependencies['todayLocal'];
  scrollIframeToTop: ProfileHostDependencies['scrollIframeToTop'];
  consentTranslationParams: ProfileHostDependencies['consentTranslationParams'];
  translatePlanDuration: ProfileHostDependencies['translatePlanDuration'];
};

/** Pass-through host. Profile cancel and PAYG purchase HTTP live in `profilePurchase`. */
export function createProfileHost(dependencies: ProfileHostDependencies): ProfileHostPorts {
  return {
    isReactProfileDialogEnabled: () => true,
    isFeatureFlagEnabled: (name) => dependencies.isFeatureFlagEnabled(name),
    getCurrentUser: () => dependencies.getCurrentUser(),
    getCurrentBranch: () => dependencies.getCurrentBranch(),
    modifyProfile: (userId, changes, image) => dependencies.modifyProfile(userId, changes, image),
    getUserConsentObjectUpdated: (consents) => dependencies.getUserConsentObjectUpdated(consents),
    getTransactionsById: (userId, page) => dependencies.getTransactionsById(userId, page),
    getDocumentsByUserId: (branchId, userId) => dependencies.getDocumentsByUserId(branchId, userId),
    getBranchDocuments: (branchId) => dependencies.getBranchDocuments(branchId),
    getBookingsById: (userId, includeSpotBookings) =>
      dependencies.getBookingsById(userId, includeSpotBookings),
    getLinkedAccount: (userId) => dependencies.getLinkedAccount(userId),
    getUsersStackedMemberships: (branchId, userId) =>
      dependencies.getUsersStackedMemberships(branchId, userId),
    cancelBookingById: (userId, bookingId) => dependencies.cancelBookingById(userId, bookingId),
    buyMembershipPlan: (...args) => dependencies.buyMembershipPlan(...args),
    logOut: () => dependencies.logOut(),
    showModal: (options) => dependencies.showModal(options),
    triggerAnalytics: (event) => dependencies.triggerAnalytics(event),
    onUserChange: (callback) => dependencies.onUserChange(callback),
    onFeatureFlagsLoaded: (callback) => dependencies.onFeatureFlagsLoaded(callback),
    getCancelRequestIds: () => dependencies.getCancelRequestIds(),
    setCancelRequestIds: (value) => dependencies.setCancelRequestIds(value),
    onCancelRequestIdsChange: (callback) => dependencies.onCancelRequestIdsChange(callback),
    fetchAllFeatureFlags: () => dependencies.fetchAllFeatureFlags(),
    getAvailableCreditsById: (userId) => dependencies.getAvailableCreditsById(userId),
    getBranchById: (branchId) => dependencies.getBranchById(branchId),
    getTermsAndConditions: (branchId, type) => dependencies.getTermsAndConditions(branchId, type),
    getUserConsents: (userId) => dependencies.getUserConsents(userId),
    hydrateChannelConsentPurposeStatuses: (channel) =>
      dependencies.hydrateChannelConsentPurposeStatuses(channel),
    deleteCardDetails: (userId) => dependencies.deleteCardDetails(userId),
    deleteDirectDebitDetails: (userId, mandateId) =>
      dependencies.deleteDirectDebitDetails(userId, mandateId),
    getUserById: (userId) => dependencies.getUserById(userId),
    setCurrentUser: (user) => dependencies.setCurrentUser(user),
    setStoredUser: (user) => dependencies.setStoredUser(user),
    getChildAccountToken: (parentId, childId) => dependencies.getChildAccountToken(parentId, childId),
    setCurrentToken: (token, kind) => dependencies.setCurrentToken(token, kind),
    removeChildToken: () => dependencies.removeChildToken(),
    setPaymentMethods: () => dependencies.setPaymentMethods(),
    emitUserChange: () => dependencies.emitUserChange(),
    emitLoading: (payload) => dependencies.emitLoading(payload),
    getDocumentUrl: (branchId, userId, documentId, userMembershipId) =>
      dependencies.getDocumentUrl(branchId, userId, documentId, userMembershipId),
    resolveBookedSpotName: (booking) => dependencies.resolveBookedSpotName(booking),
    isSpotBookingEnabledForBooking: (booking) => dependencies.isSpotBookingEnabledForBooking(booking),
    formatUnix: (timestamp, format) => dependencies.formatUnix(timestamp, format),
    formatLocalDate: (value, format) => dependencies.formatLocalDate(value, format),
    formatMoney: (amount, currency) => dependencies.formatMoney(amount, currency),
    ordinal: (value) => dependencies.ordinal(value),
    currencySymbol: () => dependencies.currencySymbol(),
    todayLocal: () => dependencies.todayLocal(),
    scrollIframeToTop: () => dependencies.scrollIframeToTop(),
    consentTranslationParams: () => dependencies.consentTranslationParams(),
    translatePlanDuration: (value, unit) => dependencies.translatePlanDuration(value, unit),
  };
}
